import React, { useEffect, useMemo, useState } from "react";
import { MessageCircle, X, Send, Loader2, Lightbulb, Bug, Heart, HelpCircle } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

/**
 * FeedbackWidget — a floating pill (bottom-right, offset from the AI bubble)
 * that opens a modal for users to submit app feedback. Also exports a hook to
 * open the modal from other trigger points (Footer link, admin nudge, etc).
 *
 * Storage: POST /api/feedback → user_feedback collection. Anonymous submissions
 * are allowed (3/hr per IP). Admin sees everything in /admin.
 */

const KINDS = [
  { key: "suggestion", label: "Suggestion", icon: Lightbulb, color: "#ff4500" },
  { key: "bug", label: "Bug", icon: Bug, color: "#dc2626" },
  { key: "praise", label: "Praise", icon: Heart, color: "#059669" },
  { key: "other", label: "Other", icon: HelpCircle, color: "#666" },
];

export function FeedbackButton({ label = "Feedback" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 z-40 inline-flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-[#0e0e0e] text-white hover:bg-[#ff4500] text-xs mono uppercase tracking-wider font-bold shadow-[0_6px_0_0_rgba(0,0,0,0.15)] hover:shadow-[0_3px_0_0_rgba(0,0,0,0.15)] hover:translate-y-[3px] transition-all"
        data-testid="feedback-fab"
        aria-label="Give feedback"
      >
        <MessageCircle className="w-4 h-4" strokeWidth={2.25} />
        {label}
      </button>
      {open && <FeedbackModal onClose={() => setOpen(false)} />}
    </>
  );
}

export function FeedbackLink({ children = "Give feedback", className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={className || "text-xs mono uppercase tracking-wider text-[#666] hover:text-[#ff4500] underline"}
        data-testid="feedback-link"
      >
        {children}
      </button>
      {open && <FeedbackModal onClose={() => setOpen(false)} />}
    </>
  );
}

function FeedbackModal({ onClose }) {
  const { user } = useAuth();
  const [kind, setKind] = useState("suggestion");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const currentUrl = useMemo(() => {
    try { return window.location.pathname + window.location.search; } catch { return null; }
  }, []);

  useEffect(() => {
    // ESC to close
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    if (message.trim().length < 8) return toast.error("Give us at least a sentence (8+ chars)");
    setBusy(true);
    try {
      await api.post("/feedback", {
        kind,
        subject: subject.trim() || null,
        message: message.trim(),
        email: (email || "").trim() || null,
        page_url: currentUrl,
      });
      setDone(true);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="feedback-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full sm:max-w-lg max-h-[95vh] overflow-y-auto border-2 hairline-strong shadow-[0_8px_0_0_#0e0e0e] rounded-t-2xl sm:rounded-2xl">
        <header className="sticky top-0 bg-white border-b hairline px-5 py-3 flex items-center justify-between">
          <h2 className="font-display text-xl inline-flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#ff4500]" />
            {done ? "Thanks!" : "Share your feedback"}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[#f5f5f5]" data-testid="feedback-close" aria-label="Close">
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </header>

        {done ? (
          <div className="p-6 sm:p-8 text-center" data-testid="feedback-success">
            <div className="w-16 h-16 rounded-full bg-[#fff4ee] mx-auto mb-4 flex items-center justify-center">
              <Heart className="w-8 h-8 text-[#ff4500] fill-[#ff4500]" strokeWidth={2} />
            </div>
            <h3 className="font-display text-2xl mb-2">You&rsquo;re a legend.</h3>
            <p className="text-sm text-[#666] mb-5">Every note like yours makes the app better for the next camper.</p>
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] text-white text-xs mono uppercase tracking-wider font-bold"
              data-testid="feedback-done"
            >Close</button>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-[#555]">Bug, idea, or just something on your mind — we read every single one.</p>

              {/* Kind selector */}
              <div>
                <label className="eyebrow block mb-2">What&rsquo;s this about?</label>
                <div className="grid grid-cols-4 gap-2" data-testid="feedback-kind-picker">
                  {KINDS.map(({ key, label, icon: Icon, color }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setKind(key)}
                      className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 transition-colors ${kind === key ? "bg-[#0e0e0e] text-white border-[#0e0e0e]" : "bg-white hairline hover:border-[#ff4500]"}`}
                      data-testid={`feedback-kind-${key}`}
                    >
                      <Icon className="w-4 h-4" style={{ color: kind === key ? "#ff4500" : color }} strokeWidth={2} />
                      <span className="text-[10px] mono uppercase tracking-wider">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="eyebrow block mb-1.5">Subject (optional)</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value.slice(0, 140))}
                  placeholder="One-line summary"
                  className="w-full border hairline rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#ff4500]"
                  data-testid="feedback-subject"
                />
              </div>

              <div>
                <label className="eyebrow block mb-1.5">Your message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 4000))}
                  placeholder="What worked, what didn't, what you'd love to see. Don't hold back — honest is best."
                  rows={6}
                  className="w-full border hairline rounded-xl px-3 py-2 text-sm leading-relaxed resize-y focus:outline-none focus:border-[#ff4500]"
                  data-testid="feedback-message"
                  autoFocus
                />
                <div className="text-[10px] mono text-[#888] text-right mt-1">{message.length}/4000</div>
              </div>

              {!user && (
                <div>
                  <label className="eyebrow block mb-1.5">Email (only if you&rsquo;d like a reply)</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.slice(0, 200))}
                    placeholder="you@example.com"
                    className="w-full border hairline rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#ff4500]"
                    data-testid="feedback-email"
                  />
                </div>
              )}
            </div>

            <footer className="sticky bottom-0 bg-white border-t hairline px-5 py-3 flex items-center justify-between gap-3">
              <button onClick={onClose} className="text-sm mono underline text-[#666]">Cancel</button>
              <button
                onClick={submit}
                disabled={busy || message.trim().length < 8}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] disabled:opacity-40 text-white text-xs mono uppercase tracking-wider font-bold"
                data-testid="feedback-submit"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={2} />}
                {busy ? "Sending…" : "Send feedback"}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

export default FeedbackButton;
